/**
 * Image export API — on-demand format conversion for editor download.
 * POST /api/export/download (multipart canvas snapshot)
 */
import apiClient, { createAuthConfig } from "../client";

/**
 * @param {string} token - Clerk session token
 * @param {Blob|File} file - Composite PNG from editor
 * @param {{ format: string, file_name?: string }} options
 * @returns {Promise<Blob>}
 */
export const downloadExportFromFile = async (token, file, options) => {
  const formData = new FormData();
  formData.append("file", file, "composite.png");
  formData.append("format", options.format);
  if (options.file_name) {
    formData.append("file_name", options.file_name);
  }
  if (options.export_size) {
    formData.append("export_size", options.export_size);
  }
  if (options.creation_id != null) {
    formData.append("creation_id", String(options.creation_id));
  }
  if (options.parent_creation_id != null) {
    formData.append("parent_creation_id", String(options.parent_creation_id));
  }
  if (options.transparent_source_id != null) {
    formData.append(
      "transparent_source_id",
      String(options.transparent_source_id)
    );
  }
  if (options.root_creation_id != null) {
    formData.append("root_creation_id", String(options.root_creation_id));
  }

  const auth = createAuthConfig(token);
  const response = await apiClient.post("/api/export/download", formData, {
    ...auth,
    responseType: "blob",
    timeout: 120000,
    headers: {
      ...auth.headers,
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

/**
 * Trigger a browser download from export blob.
 * @param {Blob} blob
 * @param {string} fileName - e.g. "product.jpg"
 */
export const saveBlobAsFile = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Extract error message from failed blob export responses.
 * @param {unknown} err
 * @returns {Promise<string>}
 */
export const getExportErrorMessage = async (err) => {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      if (parsed?.detail) {
        return typeof parsed.detail === "string"
          ? parsed.detail
          : "Download failed.";
      }
    } catch {
      return "Download failed.";
    }
  }
  return (
    err?.response?.data?.detail ||
    err?.message ||
    "Download failed. Please try again."
  );
};

export { createShareFromFile, getSharedImage, copyTextToClipboard, getShareErrorMessage } from "./share";

const exportApi = {
  downloadExportFromFile,
  saveBlobAsFile,
  getExportErrorMessage,
};

export default exportApi;
